import {once} from 'lodash';
import {shallowEqual} from 'react-redux';

import {logHandledError} from '../../services/errors/errorReporting';
import {
  type LineProperties,
  type Mark,
  type Point,
  type Timestep,
} from '../../util/plotHelpers/types';
import {type SafePoint} from '../PanelRunsLinePlot/componentOutliers/types';

export type ChartStatistic = {data: Point[]; stddev: number | undefined};

/** Borrowed snapshots. Publishing a numeric change requires a new array. */
export type ChartGeometry = {
  data: Point[];
  nanPoints: SafePoint[] | undefined;
  domain: {minX: number; maxX: number; minY: number; maxY: number} | undefined;
  timestep: Timestep | undefined;
  smoothed: boolean;
  smoothedFromBackend: boolean;
  statistics: {
    minmax: ChartStatistic | undefined;
    stddev: ChartStatistic | undefined;
    stderr: ChartStatistic | undefined;
  };
};

/** Transformation history does not determine drawable kind. */
export type ChartProvenance = {
  aggregation: LineProperties['aggregation'];
  category: LineProperties['category'];
  mode: LineProperties['mode'];
};

export type ChartSeriesStyle = {
  color: string | undefined;
  lineWidth: number | undefined;
  mark: Mark | undefined;
  /** Existing legend override key; independent of scoped drawable identity. */
  colorKey: string;
};

/** Scalar descriptions only: no Run, query envelope, JSX, or captured callbacks. */
export type ChartMetadata = {
  displayName: string | undefined;
  title: string | undefined;
  runName: string | undefined;
  isPinned: boolean;
  legendHref: string | undefined;
  entityName: string | undefined;
  projectName: string | undefined;
};

type SeriesDomain =
  | {
      domainMinX: number;
      domainMaxX: number;
      domainMinY: number;
      domainMaxY: number;
    }
  | {
      domainMinX: undefined;
      domainMaxX: undefined;
      domainMinY: undefined;
      domainMaxY: undefined;
    };

/** Flat drawable facts. History fields explicitly identify the borrowed representation. */
export type ChartSeries = {
  seriesId: string;
  metadataId: string;
  /** Legacy cross-panel highlight equivalence, independent of drawable scope. */
  highlightId: string | null;
  /** Legacy name alias also matched by cross-panel highlighting. */
  highlightName: string | null;
  /** Sample source-member key; null for other roles or an absent key. */
  memberId: string | null;
  parentSeriesId: string | null;
  role: 'primary' | 'original' | 'band' | 'sample';
  /** Sole drawable-kind authority; undefined preserves the React-Vis fallback. */
  kind: 'line' | 'area' | 'heatmap' | undefined;
  metricName: string | undefined;
  isBaseline: boolean;
  isGrouped: boolean;
  nanPoints: SafePoint[] | undefined;
  timestep: Timestep | undefined;
  smoothed: boolean;
  smoothedFromBackend: boolean;
  minmaxDeviation: number | undefined;
  stddevDeviation: number | undefined;
  stderrDeviation: number | undefined;
  historySeries: Point[];
  historyMinMax: Point[] | undefined;
  historyStdDev: Point[] | undefined;
  historyStdErr: Point[] | undefined;
} & ChartProvenance &
  SeriesDomain;

/** Internal preparation can use a coordinate bundle; publication stays flat. */
export function flattenChartGeometry(geometry: ChartGeometry) {
  const domain: SeriesDomain =
    geometry.domain == null
      ? {
          domainMinX: undefined,
          domainMaxX: undefined,
          domainMinY: undefined,
          domainMaxY: undefined,
        }
      : {
          domainMinX: geometry.domain.minX,
          domainMaxX: geometry.domain.maxX,
          domainMinY: geometry.domain.minY,
          domainMaxY: geometry.domain.maxY,
        };
  return {
    ...domain,
    historySeries: geometry.data,
    historyMinMax: geometry.statistics.minmax?.data,
    historyStdDev: geometry.statistics.stddev?.data,
    historyStdErr: geometry.statistics.stderr?.data,
    minmaxDeviation: geometry.statistics.minmax?.stddev,
    stddevDeviation: geometry.statistics.stddev?.stddev,
    stderrDeviation: geometry.statistics.stderr?.stddev,
    nanPoints: geometry.nanPoints,
    timestep: geometry.timestep,
    smoothed: geometry.smoothed,
    smoothedFromBackend: geometry.smoothedFromBackend,
  };
}

/** Return the borrowed main history, including empty arrays. */
export function getSeriesHistory(series: ChartSeries): Point[] {
  return series.historySeries;
}

/** Replace the borrowed main history without changing other drawable facts. */
export function withSeriesHistory(
  series: ChartSeries,
  data: Point[]
): ChartSeries {
  return {...series, historySeries: data};
}

export type ChartData = {
  series: ChartSeries[];
  seriesStylesById: Record<string, ChartSeriesStyle>;
  seriesMetadataById: Record<string, ChartMetadata>;
};

/** Flat numeric inputs for filtering, domains, clamping, facets, and crosshair lookup. */
export type NumericLine = Pick<
  ChartSeries,
  | 'seriesId'
  | 'metadataId'
  | 'highlightId'
  | 'highlightName'
  | 'memberId'
  | 'parentSeriesId'
  | 'role'
  | 'kind'
  | 'metricName'
  | 'isBaseline'
  | 'isGrouped'
> &
  Omit<ChartGeometry, 'statistics'> & {
    provenance: ChartProvenance;
    minmaxLine: ChartStatistic | undefined;
    stddevLine: ChartStatistic | undefined;
    stderrLine: ChartStatistic | undefined;
  };

/** Canvas drawing depends on geometry and style, independently of descriptions. */
export type DrawLine = NumericLine & ChartSeriesStyle;

/** Presentation joined at a consumer; numeric fields remain authoritative. */
export type RenderLine = NumericLine & ChartSeriesStyle & ChartMetadata;

export function shallowReuse<T extends object>(value: T, previous?: T): T {
  return previous !== undefined && shallowEqual(value, previous)
    ? previous
    : value;
}

// One diagnostic per page load, even if a host republishes the same collision.
const reportDuplicateSeries = once((seriesCount: number) => {
  logHandledError('Duplicate chart series ID', {extra: {seriesCount}});
});

/** Report unexpected drawable collisions without rejecting numeric inputs. */
export function resolveNumericSeries(series: ChartSeries[]): NumericLine[] {
  const ids = new Set<string>();
  return series.map(item => {
    if (ids.has(item.seriesId)) {
      reportDuplicateSeries(series.length);
    }
    ids.add(item.seriesId);
    function statistic(
      history: Point[] | undefined,
      stddev: number | undefined
    ): ChartStatistic | undefined {
      return history === undefined ? undefined : {data: history, stddev};
    }
    return {
      seriesId: item.seriesId,
      metadataId: item.metadataId,
      highlightId: item.highlightId,
      highlightName: item.highlightName,
      memberId: item.memberId,
      parentSeriesId: item.parentSeriesId,
      role: item.role,
      kind: item.kind,
      metricName: item.metricName,
      isBaseline: item.isBaseline,
      isGrouped: item.isGrouped,
      provenance: {
        aggregation: item.aggregation,
        category: item.category,
        mode: item.mode,
      },
      data: getSeriesHistory(item),
      nanPoints: item.nanPoints,
      domain:
        item.domainMinX === undefined
          ? undefined
          : {
              minX: item.domainMinX,
              maxX: item.domainMaxX!,
              minY: item.domainMinY!,
              maxY: item.domainMaxY!,
            },
      timestep: item.timestep,
      smoothed: item.smoothed,
      smoothedFromBackend: item.smoothedFromBackend,
      minmaxLine: statistic(item.historyMinMax, item.minmaxDeviation),
      stddevLine: statistic(item.historyStdDev, item.stddevDeviation),
      stderrLine: statistic(item.historyStdErr, item.stderrDeviation),
    };
  });
}

const emptyMetadata: ChartMetadata = {
  displayName: undefined,
  title: '',
  runName: undefined,
  isPinned: false,
  legendHref: undefined,
  entityName: undefined,
  projectName: undefined,
};
const emptyStyle: ChartSeriesStyle = {
  color: undefined,
  lineWidth: undefined,
  mark: undefined,
  colorKey: '',
};

/** Missing descriptions and style do not hide geometry. */
export function resolveChartSeries({
  series,
  seriesStylesById,
  seriesMetadataById,
}: ChartData): RenderLine[] {
  return presentChartSeries(
    resolveNumericSeries(series),
    seriesStylesById,
    seriesMetadataById
  );
}

/** Style is the only presentation branch used by native drawing layers. */
export function styleChartSeries(
  numeric: NumericLine[],
  seriesStylesById: ChartData['seriesStylesById']
): DrawLine[] {
  return numeric.map(line => ({
    ...(seriesStylesById[line.seriesId] ?? emptyStyle),
    ...line,
  }));
}

/** Presentation can change without projecting numeric series again. */
export function presentChartSeries(
  numeric: NumericLine[],
  seriesStylesById: ChartData['seriesStylesById'],
  seriesMetadataById: ChartData['seriesMetadataById']
): RenderLine[] {
  return numeric.map(line => ({
    ...(seriesMetadataById[line.metadataId] ?? emptyMetadata),
    ...(seriesStylesById[line.seriesId] ?? emptyStyle),
    ...line,
  }));
}

/** Join by required drawable ID, preserving a filtered or clamped numeric projection. */
export function presentNumericLines(
  numeric: NumericLine[],
  presentation: RenderLine[]
): RenderLine[] {
  const byId = new Map(presentation.map(line => [line.seriesId, line]));
  return numeric.map(line => ({
    ...emptyMetadata,
    ...emptyStyle,
    ...byId.get(line.seriesId),
    ...line,
  }));
}
